/**
 * TanStack Query hooks for the equipment catalog: Technogym devices, the catalog, the brands and
 * their imports.
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  TechnogymDeviceOut,
  BrandSummaryOut,
  EquipmentCatalogOut,
  EquipmentImportResult,
} from '../types'

/**
 * Fetch the Technogym equipment overview (devices + usage stats).
 */
export function useTechnogymDevices(enabled = true) {
  return useQuery({
    queryKey: ['technogym-devices'],
    queryFn: () => apiFetch<TechnogymDeviceOut[]>('/api/technogym/devices'),
    enabled,
  })
}

// ── Technogym equipment catalog ──────────────────────────────────────────

/** The imported gym equipment catalog, grouped (empty until imported). */
export function useEquipmentCatalog() {
  return useQuery({
    queryKey: ['technogym', 'equipment'],
    queryFn: () => apiFetch<EquipmentCatalogOut>('/api/technogym/equipment'),
  })
}

/**
 * The brands in the catalog, plus those that could be imported into it (Dr. Wolff
 * and anything shaped like it ships as a checked-in seed), with what is already
 * imported for each. Technogym's row has no seed (`importable` is false): it is
 * imported live, through `useImportEquipment`.
 * Query key: ['equipment', 'brands']
 */
export function useBrandCatalogs() {
  return useQuery({
    queryKey: ['equipment', 'brands'],
    queryFn: () => apiFetch<BrandSummaryOut[]>('/api/equipment/brands'),
  })
}

/**
 * The two reads that answer, per caller, what is in their equipment catalog and
 * how much of it they declared at a gym (ADR 0012, #383):
 * `useEquipmentCatalog` (Settings > Gym Equipment, the Knowledge Base's Machines
 * tab) and `useBrandCatalogs` (its brand rows' "at your gym" counts). The global
 * `staleTime` is Infinity, so after anything that changes the catalog or a
 * declaration they keep showing the old answer unless asked again; without that,
 * "No equipment yet > Set up your gym" would send a user to declare their gym and
 * bring them back to a screen that still says they have none (#521).
 *
 * Called by every write that changes either: a brand import (Technogym's or a
 * seeded one's), a gym's equipment
 * save, and a gym's deletion. Two named keys tied to those writes, not a broad
 * invalidation.
 */
export function invalidateEquipmentReads(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['technogym', 'equipment'] })
  void qc.invalidateQueries({ queryKey: ['equipment', 'brands'] })
}

/**
 * Import Technogym's whole equipment catalog with the login stored in Settings >
 * Credentials (#522). Needs `catalog.curate` server-side.
 *
 * `scope=catalog`, the whole of what Technogym sells, and never `facility`: since
 * #383 every user picks their own gym's equipment from this shared catalog, so it has
 * to be complete, and a facility-scope run replaces each machine's exercises with the
 * admin's own gym's lists. (Before #382 the button sent no scope and got `facility`,
 * which was right while the catalog was one person's gym.) The route has no default.
 */
export function useImportEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<EquipmentImportResult>('/api/technogym/equipment/import?scope=catalog', {
        method: 'POST',
      }),
    onSuccess: () => invalidateEquipmentReads(qc),
  })
}

/**
 * Import one seeded brand's catalog. Needs `catalog.curate` server-side. It makes no claim
 * about any gym: which machines are at a gym is what its user declared.
 */
export function useImportBrandCatalog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<EquipmentImportResult>(
        `/api/equipment/${encodeURIComponent(slug)}/import`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateEquipmentReads(qc),
  })
}
